import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import { DatePicker } from "@/shared/ui/DatePicker";
import { Input } from "@/components/ui/input";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type RefundQueueSearch = {
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  offset?: number;
  limit?: number;
};

export function RefundQueueFilter() {
  const nav = useNavigate({ from: "/app/wallet/refund-queue" });
  const search = useSearch({
    from: "/app/wallet/refund-queue",
  }) as RefundQueueSearch;

  const hasFilter =
    !!search.createdFrom || !!search.createdTo || !!search.search;

  const [searchInputValue, setSearchInputValue] = useState<string>(
    search.search ?? "",
  );
  useEffect(() => {
    setSearchInputValue(search.search ?? "");
  }, [search.search]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchInputValue(val);
    update({ search: val || undefined });
  }, 400);

  const update = (next: Partial<RefundQueueSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    setSearchInputValue("");
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <DatePicker
        mode="range"
        value={{
          from: search.createdFrom
            ? parseISO(search.createdFrom)
            : undefined,
          to: search.createdTo ? parseISO(search.createdTo) : undefined,
        }}
        onChange={(range) => {
          const from = Array.isArray(range)
            ? range[0]
            : (range as { from?: Date; to?: Date })?.from;
          const to = Array.isArray(range)
            ? range[1]
            : (range as { from?: Date; to?: Date })?.to;
          update({
            createdFrom: from ? formatDateForInput(from) : undefined,
            createdTo: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ວັນທີຂໍຄືນ"
        className="h-9 w-fit min-w-56"
      />
      <Input
        type="search"
        placeholder="ຄົ້ນຫາ order / ລູກຄ້າ"
        className="w-56"
        defaultValue={searchInputValue}
        onChange={(e) => {
          debouncedSearch(e.target.value);
        }}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
